import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { Constants } from "../../../assets/utils/lib/constants";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { LookupDto } from "../../../assets/utils/lib/lookupDto";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { CommunityNeighborhoodDto } from "../../../services/dto";
import { IActionResponse } from "../../../services/proxies";
import { CommunityNeighborhoodForm } from "./communityNeighborhoodForm";
import { CommunityNeighborhoodPage } from "./communityNeighborhoodPage";
import { CommunityNeighborhoodService, communityNeighborhoodQueryKeys } from "./communityNeighborhoodService";

interface Props {
  isCommunityDeactive: boolean | undefined;
  counties: LookupDto[] | undefined;
  communityId: string;
}

export const CommunityNeighborhoodContainer = (props: Props) => {
  const communityId = props.communityId;
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [selectedId, setSelectedId] = useState<string>();
  const [drawerType, setDrawerType] = useState<GridActionType>(GRID_ACTION_TYPES.HIDDEN);
  const gridQuery = CommunityNeighborhoodService.getAll(communityId);
  const detailQuery = CommunityNeighborhoodService.get(selectedId);
  const { mutateAsync: save } = CommunityNeighborhoodService.save();

  const closeDrawer = () => {
    setDrawerType(GRID_ACTION_TYPES.HIDDEN);
    setSelectedId(undefined);
  };

  const onCustomEvent = (actionType: string, dataId: string) => {
    if (actionType === GRID_ACTION_TYPES.NEW) {
      setSelectedId(undefined);
      setDrawerType(GRID_ACTION_TYPES.NEW);
    } else if (actionType === GRID_ACTION_TYPES.EDIT) {
      setSelectedId(dataId);
      setDrawerType(GRID_ACTION_TYPES.EDIT);
    }
  };

  const handleSave = async (values: CommunityNeighborhoodDto) => {
    const payload: CommunityNeighborhoodDto = {
      ...values,
      id: selectedId ?? Constants.emptyGuid,
      communityId,
      zipCodes: values.zipCodes ?? [],
      isDeleted: values.isDeleted ?? false,
    };

    return await save(payload, {
      onSuccess: (response: IActionResponse) => {
        const error = apiErrorMessage(response.errorMessages);
        if (error) {
          showSnackbar(error, MessageSeverity.Error);
          return;
        }
        showSnackbar("Neighborhood Saved", MessageSeverity.Success);
        queryClient.invalidateQueries({ queryKey: [communityNeighborhoodQueryKeys.all, communityId] });
        closeDrawer();
      },
      onError: () => showSnackbar("Failed to save neighborhood", MessageSeverity.Error),
    });
  };

  return (
    <>
      <CommunityNeighborhoodPage
        data={gridQuery.data ?? []}
        loading={gridQuery.isLoading || gridQuery.isFetching}
        isCommunityDeactive={props.isCommunityDeactive}
        onCustomEvent={onCustomEvent}
      />
      {(drawerType === GRID_ACTION_TYPES.NEW || drawerType === GRID_ACTION_TYPES.EDIT) && (
        <CommunityNeighborhoodForm
          data={drawerType === GRID_ACTION_TYPES.EDIT ? detailQuery.data : undefined}
          show
          counties={props.counties}
          isLoading={detailQuery.isLoading}
          onHide={closeDrawer}
          onSave={handleSave}
        />
      )}
    </>
  );
};
